import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import Dropdown from "components/CustomDropdown/Dropdown";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "utils/api";

const TYPES = [
  { value: "NOTES", label: "Notes" },
  { value: "EXAM_PAPER", label: "Exam paper" },
  { value: "QUIZ", label: "Quiz" },
  { value: "SYLLABUS", label: "Syllabus" },
  { value: "TIMETABLE", label: "Timetable" },
];

const AUDIENCES = [
  { value: "CLASS", label: "Class, board and subject" },
  { value: "SESSIONS", label: "Specific classes only" },
  { value: "STUDENTS", label: "Specific students only" },
  { value: "ALL", label: "All parents" },
];

const LANGUAGES = [
  { value: "ENGLISH", label: "English" },
  { value: "HINDI", label: "Hindi" },
  { value: "BOTH", label: "Hindi and English" },
];

const STATUSES = [
  { value: "DRAFT", label: "Draft" },
  { value: "PUBLISHED", label: "Published" },
  { value: "HIDDEN", label: "Hidden" },
];

const emptyForm = {
  title: "",
  description: "",
  type: "NOTES",
  audienceType: "CLASS",
  classId: "",
  boardId: "",
  subjectId: "",
  chapter: "",
  language: "ENGLISH",
  status: "DRAFT",
  isPinned: false,
  showFrom: "",
  hideAfter: "",
  solutionVisibleFrom: "",
  quizDurationMinutes: "10",
};

const blankQuestion = () => ({
  prompt: "",
  options: ["", "", "", ""],
  correctIndex: 0,
  marks: 1,
});

const toLocalInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (part) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const idOf = (value) => (value && typeof value === "object" ? value._id : value || "");

function StudyMaterialForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(id);
  const [form, setForm] = useState(emptyForm);
  const [meta, setMeta] = useState({ classes: [], boards: [], subjects: [] });
  const [files, setFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [removedFileIds, setRemovedFileIds] = useState([]);
  const [solutionFile, setSolutionFile] = useState(null);
  const [existingSolution, setExistingSolution] = useState(null);
  const [removeSolution, setRemoveSolution] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [targetSearch, setTargetSearch] = useState("");
  const [targetResults, setTargetResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [questions, setQuestions] = useState([blankQuestion()]);
  const [attempts, setAttempts] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const metaResponse = await api.get("/study-materials/meta");
        setMeta(metaResponse.data.data || { classes: [], boards: [], subjects: [] });
        if (!id) return;
        const response = await api.get(`/study-materials/${id}`);
        const item = response.data.data;
        setForm({
          title: item.title || "",
          description: item.description || "",
          type: item.type || "NOTES",
          audienceType: item.audienceType || "CLASS",
          classId: idOf(item.classId),
          boardId: idOf(item.boardId),
          subjectId: idOf(item.subjectId),
          chapter: item.chapter || "",
          language: item.language || "ENGLISH",
          status: item.status === "SCHEDULED" ? "PUBLISHED" : item.status || "DRAFT",
          isPinned: !!item.isPinned,
          showFrom: toLocalInput(item.showFrom),
          hideAfter: toLocalInput(item.hideAfter),
          solutionVisibleFrom: toLocalInput(item.solutionVisibleFrom),
          quizDurationMinutes: String(item.quizDurationMinutes || 10),
        });
        const loadedQuestions = (item.quizQuestions || []).map((question) => ({
          prompt: question.prompt || "",
          options: [...(question.options || []), "", "", "", ""].slice(0, 4),
          correctIndex: question.correctIndex || 0,
          marks: question.marks || 1,
        }));
        setQuestions(loadedQuestions.length ? loadedQuestions : [blankQuestion()]);
        if (item.type === "QUIZ") {
          const attemptResponse = await api.get(`/study-materials/${id}/attempts`);
          setAttempts(attemptResponse.data.data || []);
        }
        setExistingFiles(item.files || []);
        setExistingSolution(item.solutionFile || null);
        setSelectedStudents(
          (item.studentIds || []).map((student) => ({
            id: idOf(student),
            label: student.name || "Student",
          })),
        );
        setSelectedSessions(
          (item.classSessionIds || []).map((session) => ({
            id: idOf(session),
            label: [session.parentId?.fullName, session.tutorId?.fullName].filter(Boolean).join(" · ") || "Class",
          })),
        );
      } catch (error) {
        toast.error(error.response?.data?.message || "Could not open this study material");
        navigate("/study-hub");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate]);

  const subjectOptions = useMemo(() => {
    const list = (meta.subjects || []).filter((subject) => {
      if (!form.classId) return true;
      return (subject.classes || []).map(String).includes(String(form.classId));
    });
    return [{ value: "", label: "All subjects" }, ...list.map((subject) => ({ value: subject._id, label: subject.name }))];
  }, [meta.subjects, form.classId]);

  const setField = (name, value) => setForm((current) => ({ ...current, [name]: value }));

  const searchTargets = async () => {
    if (targetSearch.trim().length < 2) {
      toast.error("Type at least 2 letters");
      return;
    }
    try {
      const kind = form.audienceType === "SESSIONS" ? "sessions" : "students";
      const response = await api.get("/study-materials/targets", {
        params: { kind, search: targetSearch.trim() },
      });
      setTargetResults(response.data.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Search failed");
    }
  };

  const addTarget = (target) => {
    const setter = form.audienceType === "SESSIONS" ? setSelectedSessions : setSelectedStudents;
    setter((current) => (current.some((item) => item.id === target.id) ? current : [...current, target]));
  };

  const handleFiles = (event) => {
    const picked = Array.from(event.target.files || []);
    const accepted = picked.filter((file) => {
      const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
      const max = isPdf ? 20 * 1024 * 1024 : 5 * 1024 * 1024;
      if (file.size > max) {
        toast.error(`${file.name} is too large`);
        return false;
      }
      return true;
    });
    setFiles(accepted);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    const keptFiles = existingFiles.filter((file) => !removedFileIds.includes(file.id));
    if (form.type !== "QUIZ" && !editing && files.length === 0) {
      toast.error("Upload at least one file");
      return;
    }
    if (form.type !== "QUIZ" && editing && keptFiles.length + files.length === 0) {
      toast.error("Keep or upload at least one file");
      return;
    }
    if (form.audienceType === "CLASS" && !form.classId) {
      toast.error("Select a class");
      return;
    }
    if (form.audienceType === "STUDENTS" && selectedStudents.length === 0) {
      toast.error("Select at least one student");
      return;
    }
    if (form.audienceType === "SESSIONS" && selectedSessions.length === 0) {
      toast.error("Select at least one class");
      return;
    }
    const packedQuestions = questions.map((question) => {
      const filled = question.options
        .map((text, index) => ({ text: text.trim(), index }))
        .filter((option) => option.text);
      return {
        prompt: question.prompt,
        options: filled.map((option) => option.text),
        correctIndex: filled.findIndex((option) => option.index === Number(question.correctIndex)),
        marks: Number(question.marks) || 1,
      };
    });
    if (form.type === "QUIZ" && packedQuestions.length === 0) {
      toast.error("Add at least one objective question");
      return;
    }

    const body = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (key === "showFrom" || key === "hideAfter" || key === "solutionVisibleFrom") {
        body.append(key, value ? new Date(value).toISOString() : "");
        return;
      }
      body.append(key, value);
    });
    body.append("studentIds", JSON.stringify(selectedStudents.map((item) => item.id)));
    body.append("classSessionIds", JSON.stringify(selectedSessions.map((item) => item.id)));
    body.append("removeFileIds", JSON.stringify(removedFileIds));
    body.append("removeSolution", removeSolution ? "true" : "false");
    body.append("quizQuestions", JSON.stringify(form.type === "QUIZ" ? packedQuestions : []));
    files.forEach((file) => body.append("files", file));
    if (solutionFile) body.append("solutionFile", solutionFile);

    try {
      setSaving(true);
      const response = editing
        ? await api.put(`/study-materials/${id}`, body, { headers: { "Content-Type": "multipart/form-data" } })
        : await api.post("/study-materials", body, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(response.data.message || "Saved");
      navigate("/study-hub");
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const selectedTargets = form.audienceType === "SESSIONS" ? selectedSessions : selectedStudents;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Card>
          <ArgonBox p={3} component="form" onSubmit={handleSubmit}>
            <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
              {editing ? "Edit study material" : "Add study material"}
            </ArgonTypography>
            {loading ? (
              <ArgonBox display="flex" justifyContent="center" p={3}>
                <CircularProgress color="info" />
              </ArgonBox>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="caption" fontWeight="medium">Title</ArgonTypography>
                  <ArgonInput name="title" value={form.title} onChange={(event) => setField("title", event.target.value)} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <ArgonTypography variant="caption" fontWeight="medium">Type</ArgonTypography>
                  <Dropdown options={TYPES} value={form.type} onChange={(event) => setField("type", event.target.value)} width="100%" />
                </Grid>
                <Grid item xs={12} md={3}>
                  <ArgonTypography variant="caption" fontWeight="medium">Status</ArgonTypography>
                  <Dropdown options={STATUSES} value={form.status} onChange={(event) => setField("status", event.target.value)} width="100%" />
                </Grid>
                <Grid item xs={12}>
                  <ArgonTypography variant="caption" fontWeight="medium">Description</ArgonTypography>
                  <ArgonInput multiline rows={3} value={form.description} onChange={(event) => setField("description", event.target.value)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <ArgonTypography variant="caption" fontWeight="medium">Who can see this</ArgonTypography>
                  <Dropdown options={AUDIENCES} value={form.audienceType} onChange={(event) => setField("audienceType", event.target.value)} width="100%" />
                </Grid>
                <Grid item xs={12} md={4}>
                  <ArgonTypography variant="caption" fontWeight="medium">Chapter</ArgonTypography>
                  <ArgonInput value={form.chapter} onChange={(event) => setField("chapter", event.target.value)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <ArgonTypography variant="caption" fontWeight="medium">Language</ArgonTypography>
                  <Dropdown options={LANGUAGES} value={form.language} onChange={(event) => setField("language", event.target.value)} width="100%" />
                </Grid>

                {form.audienceType === "CLASS" && (
                  <>
                    <Grid item xs={12} md={4}>
                      <ArgonTypography variant="caption" fontWeight="medium">Class</ArgonTypography>
                      <Dropdown
                        placeholder="Select class"
                        options={(meta.classes || []).map((item) => ({ value: item._id, label: item.name }))}
                        value={form.classId}
                        onChange={(event) => setForm((current) => ({ ...current, classId: event.target.value, subjectId: "" }))}
                        width="100%"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <ArgonTypography variant="caption" fontWeight="medium">Board</ArgonTypography>
                      <Dropdown
                        options={[{ value: "", label: "All boards" }, ...(meta.boards || []).map((item) => ({ value: item._id, label: item.name }))]}
                        value={form.boardId}
                        onChange={(event) => setField("boardId", event.target.value)}
                        width="100%"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <ArgonTypography variant="caption" fontWeight="medium">Subject</ArgonTypography>
                      <Dropdown
                        options={subjectOptions}
                        value={form.subjectId}
                        onChange={(event) => setField("subjectId", event.target.value)}
                        width="100%"
                      />
                    </Grid>
                  </>
                )}

                {(form.audienceType === "STUDENTS" || form.audienceType === "SESSIONS") && (
                  <Grid item xs={12}>
                    <ArgonTypography variant="caption" fontWeight="medium">
                      {form.audienceType === "STUDENTS" ? "Search student" : "Search parent or student"}
                    </ArgonTypography>
                    <ArgonBox display="flex" gap={1} mb={1}>
                      <ArgonInput value={targetSearch} onChange={(event) => setTargetSearch(event.target.value)} placeholder="Type a name" />
                      <ArgonButton type="button" color="info" onClick={searchTargets}>Search</ArgonButton>
                    </ArgonBox>
                    {targetResults.map((target) => (
                      <ArgonButton key={target.id} type="button" variant="outlined" color="secondary" size="small" sx={{ mr: 1, mb: 1 }} onClick={() => addTarget(target)}>
                        {target.label}
                      </ArgonButton>
                    ))}
                    <ArgonBox mt={1}>
                      {selectedTargets.map((target) => (
                        <ArgonButton
                          key={target.id}
                          type="button"
                          color="primary"
                          size="small"
                          sx={{ mr: 1, mb: 1 }}
                          onClick={() => {
                            const setter = form.audienceType === "SESSIONS" ? setSelectedSessions : setSelectedStudents;
                            setter((current) => current.filter((item) => item.id !== target.id));
                          }}
                        >
                          {target.label} ×
                        </ArgonButton>
                      ))}
                    </ArgonBox>
                  </Grid>
                )}

                {form.audienceType === "ALL" && (
                  <Grid item xs={12}>
                    <ArgonTypography variant="caption" color="text">
                      Every parent with a student will see this when it is published.
                    </ArgonTypography>
                  </Grid>
                )}

                <Grid item xs={12} md={4}>
                  <ArgonTypography variant="caption" fontWeight="medium">Show from</ArgonTypography>
                  <ArgonInput type="datetime-local" value={form.showFrom} onChange={(event) => setField("showFrom", event.target.value)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <ArgonTypography variant="caption" fontWeight="medium">Hide after</ArgonTypography>
                  <ArgonInput type="datetime-local" value={form.hideAfter} onChange={(event) => setField("hideAfter", event.target.value)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <ArgonTypography variant="caption" fontWeight="medium">Answer key opens on</ArgonTypography>
                  <ArgonInput type="datetime-local" value={form.solutionVisibleFrom} onChange={(event) => setField("solutionVisibleFrom", event.target.value)} />
                </Grid>

                {form.type === "QUIZ" && (
                  <Grid item xs={12}>
                    <ArgonTypography variant="caption" fontWeight="medium">Timer (minutes)</ArgonTypography>
                    <ArgonInput
                      type="number"
                      value={form.quizDurationMinutes}
                      onChange={(event) => setField("quizDurationMinutes", event.target.value)}
                    />
                    <ArgonBox mt={2}>
                      {questions.map((question, questionIndex) => (
                        <ArgonBox key={questionIndex} mb={2} p={2} sx={{ border: "1px solid #d2d6da", borderRadius: "12px" }}>
                          <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <ArgonTypography variant="button" fontWeight="medium">Question {questionIndex + 1}</ArgonTypography>
                            <ArgonButton
                              type="button"
                              variant="text"
                              color="error"
                              size="small"
                              onClick={() => setQuestions((current) => current.filter((_, index) => index !== questionIndex))}
                            >
                              Remove
                            </ArgonButton>
                          </ArgonBox>
                          <ArgonInput
                            placeholder="Question"
                            value={question.prompt}
                            onChange={(event) => {
                              const prompt = event.target.value;
                              setQuestions((current) => current.map((item, index) => index === questionIndex ? { ...item, prompt } : item));
                            }}
                          />
                          {question.options.map((option, optionIndex) => (
                            <ArgonBox key={optionIndex} display="flex" alignItems="center" mt={1} gap={1}>
                              <input
                                type="radio"
                                name={`correct-${questionIndex}`}
                                checked={Number(question.correctIndex) === optionIndex}
                                onChange={() => {
                                  setQuestions((current) => current.map((item, index) => index === questionIndex ? { ...item, correctIndex: optionIndex } : item));
                                }}
                              />
                              <ArgonInput
                                placeholder={`Option ${optionIndex + 1}`}
                                value={option}
                                onChange={(event) => {
                                  const text = event.target.value;
                                  setQuestions((current) => current.map((item, index) => {
                                    if (index !== questionIndex) return item;
                                    const options = [...item.options];
                                    options[optionIndex] = text;
                                    return { ...item, options };
                                  }));
                                }}
                              />
                            </ArgonBox>
                          ))}
                          <ArgonBox mt={1} maxWidth={160}>
                            <ArgonTypography variant="caption">Marks</ArgonTypography>
                            <ArgonInput
                              type="number"
                              value={question.marks}
                              onChange={(event) => {
                                const marks = event.target.value;
                                setQuestions((current) => current.map((item, index) => index === questionIndex ? { ...item, marks } : item));
                              }}
                            />
                          </ArgonBox>
                        </ArgonBox>
                      ))}
                      <ArgonButton type="button" variant="outlined" color="info" size="small" onClick={() => setQuestions((current) => [...current, blankQuestion()])}>
                        Add question
                      </ArgonButton>
                    </ArgonBox>
                    {editing && (
                      <ArgonBox mt={2}>
                        <ArgonTypography variant="button" fontWeight="medium">Submitted results</ArgonTypography>
                        {attempts.length === 0 ? (
                          <ArgonTypography variant="caption" display="block" color="text">No one has submitted this quiz yet.</ArgonTypography>
                        ) : (
                          attempts.map((attempt) => (
                            <ArgonTypography key={attempt.id} variant="caption" display="block">
                              {attempt.studentName}: {attempt.score}/{attempt.totalMarks}
                            </ArgonTypography>
                          ))
                        )}
                      </ArgonBox>
                    )}
                  </Grid>
                )}

                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="caption" fontWeight="medium">Files (PDF or image)</ArgonTypography>
                  <input type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={handleFiles} />
                  {existingFiles.map((file) => (
                    <ArgonBox key={file.id} display="flex" alignItems="center" mt={1}>
                      <input
                        type="checkbox"
                        checked={removedFileIds.includes(file.id)}
                        onChange={(event) => {
                          setRemovedFileIds((current) =>
                            event.target.checked ? [...current, file.id] : current.filter((item) => item !== file.id),
                          );
                        }}
                      />
                      <ArgonTypography variant="caption" ml={1}>
                        Remove {file.fileName}
                      </ArgonTypography>
                    </ArgonBox>
                  ))}
                </Grid>
                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="caption" fontWeight="medium">Answer key file</ArgonTypography>
                  <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={(event) => setSolutionFile(event.target.files?.[0] || null)} />
                  {existingSolution && (
                    <ArgonBox display="flex" alignItems="center" mt={1}>
                      <input type="checkbox" checked={removeSolution} onChange={(event) => setRemoveSolution(event.target.checked)} />
                      <ArgonTypography variant="caption" ml={1}>
                        Remove {existingSolution.fileName}
                      </ArgonTypography>
                    </ArgonBox>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <ArgonBox display="flex" alignItems="center">
                    <input type="checkbox" checked={form.isPinned} onChange={(event) => setField("isPinned", event.target.checked)} />
                    <ArgonTypography variant="caption" ml={1}>Pin to the top of the parent list</ArgonTypography>
                  </ArgonBox>
                </Grid>
                <Grid item xs={12}>
                  <ArgonButton type="submit" color="primary" variant="contained" disabled={saving}>
                    {saving ? "Saving..." : "Save"}
                  </ArgonButton>
                  <ArgonButton type="button" variant="outlined" color="secondary" sx={{ ml: 1 }} onClick={() => navigate("/study-hub")}>
                    Cancel
                  </ArgonButton>
                </Grid>
              </Grid>
            )}
          </ArgonBox>
        </Card>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default StudyMaterialForm;
